import { createMessengerClient } from "../messenger/messengerClient";
import axios from 'axios';
import { getFingerprintForHeader, syncFingerprintFromCookie } from './clientAuth';
import {
    getStoredNativeAccessToken,
    refreshNativeSession,
} from './nativeSession';

const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || '';

let accessToken = getStoredNativeAccessToken() || null;
let accessExp = 0;
let cookieSessionValidUntil = 0;
let refreshing = false;
let waiters = [];

function setAccessToken(token) {
    accessToken = token;
    accessExp = token ? decodeExp(token) : 0;
}

function decodeExp(jwt) {
    try {
        const parts = jwt.split('.');
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return typeof payload.exp === 'number' ? payload.exp : 0;
    } catch {
        return 0;
    }
}

function isTokenExpiredSoon() {
    if (!accessToken || !accessExp) {
        return cookieSessionValidUntil - Date.now() < 30000;
    }
    return accessExp * 1000 - Date.now() < 30000;
}

const idp = axios.create({
    baseURL: IDP_ORIGIN,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    timeout: 15000,
});

async function refreshOnce(force = false) {
    if (!force && !isTokenExpiredSoon()) return true;
    if (refreshing) return new Promise((res) => waiters.push(res));
    refreshing = true;
    let succeeded = false;
    try {
        const fp = getFingerprintForHeader();
        const res = await refreshNativeSession(fp);
        syncFingerprintFromCookie();
        const token = res?.data?.access_token || res?.data?.token;
        setAccessToken(token || null);
        cookieSessionValidUntil = token ? 0 : Date.now() + 10 * 60 * 1000;
        succeeded = true;
        return true;
    } catch {
        setAccessToken(null);
        cookieSessionValidUntil = 0;
        return false;
    } finally {
        refreshing = false;
        waiters.splice(0).forEach((fn) => fn(succeeded));
    }
}

function makeIdpHttp() {
    const http = axios.create({
        baseURL: IDP_ORIGIN,
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 15000,
    });

    http.interceptors.request.use(async (config) => {
        await refreshOnce(false);
        config.headers = config.headers || {};
        if (['post', 'put', 'patch', 'delete'].includes(String(config.method || 'get').toLowerCase())) {
            const fp = getFingerprintForHeader();
            if (fp) config.headers['X-CSRF-Token'] = fp;
        }
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        } else {
            delete config.headers['Authorization'];
        }
        return config;
    });

    http.interceptors.response.use(
        (response) => {
            syncFingerprintFromCookie();
            return response;
        },
        async (error) => {
            const cfg = error.config || {};
            const status = error.response?.status;
            syncFingerprintFromCookie();

            if (status === 401 && !cfg._retried) {
                cfg._retried = true;
                const ok = await refreshOnce(true);
                if (ok) {
                    cfg.headers = cfg.headers || {};
                    if (accessToken) cfg.headers['Authorization'] = `Bearer ${accessToken}`;
                    else delete cfg.headers['Authorization'];
                    return http.request(cfg);
                }
            }

            return Promise.reject(error);
        }
    );

    return http;
}

const httpIdp = makeIdpHttp();
export const messengerClient = createMessengerClient({ baseUrl: IDP_ORIGIN, request: async (path, options = {}) => (await httpIdp.request({ ...options, url: path, method: options.method || "GET", headers: { ...options.headers, "X-Device-Fingerprint": getFingerprintForHeader() || "" } })).data });

function normalizeAxiosError(e) {
    const status = e?.response?.status;
    const details = e?.response?.data;
    const err = new Error(
        `HTTP ${status ?? '?'}${e?.message ? ` — ${e.message}` : ''}${
            details ? `: ${typeof details === 'string' ? details : JSON.stringify(details)}` : ''
        }`
    );
    err.status = status;
    err.details = details;
    throw err;
}

function resolveAssetUrl(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = IDP_ORIGIN.replace(/\/$/, '');
    return `${normalized}${path.startsWith('/') ? path : `/${path}`}`;
}

export const chatApi = {
    async listChats(limit = 40, offset = 0) {
        try {
            const res = await httpIdp.get('/api/chats', { params: { limit, offset, compactParticipants: 1 } });
            return res.data?.items ?? [];
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async getChat(chatId) {
        try {
            const res = await httpIdp.get(`/api/chats/${chatId}`);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async createChat(payload) {
        try {
            const res = await httpIdp.post('/api/chats', payload);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async inviteByPhone(chatId, phone, displayName) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/invite`, { phone, displayName });
            return res.data.participant;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async listMessages(chatId, params) {
        try {
            const res = await httpIdp.get(`/api/chats/${chatId}/messages`, { params });
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async searchMessages(chatId, params) {
        try {
            const res = await httpIdp.get(`/api/chats/${chatId}/messages/search`, { params });
            return res.data?.items ?? [];
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async sendMessage(chatId, text, options = {}) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/messages`, {
                text,
                replyToMessageId: options.replyToMessageId || undefined,
            });
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async editMessage(chatId, messageId, text) {
        try {
            const res = await httpIdp.put(`/api/chats/${chatId}/messages/${messageId}`, { text });
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async deleteMessage(chatId, messageId) {
        try {
            const res = await httpIdp.delete(`/api/chats/${chatId}/messages/${messageId}`);
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async uploadFiles(chatId, files, options = {}) {
        try {
            const formData = new FormData();
            Array.from(files || []).forEach((file) => {
                formData.append('files[]', file);
            });

            if (options.text) formData.append('text', options.text);
            if (options.kind) formData.append('kind', options.kind);
            if (options.durationSec != null) formData.append('durationSec', String(options.durationSec));
            if (options.replyToMessageId) formData.append('replyToMessageId', String(options.replyToMessageId));

            const res = await httpIdp.post(`/api/chats/${chatId}/attachments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async getChatSettings(chatId) {
        try {
            const res = await httpIdp.get(`/api/chats/${chatId}/settings`);
            return res.data?.settings;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async updateChatSettings(chatId, payload) {
        try {
            const res = await httpIdp.put(`/api/chats/${chatId}/settings`, payload);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async getChatMedia(chatId, bucket = 'media', limit = 60, offset = 0) {
        try {
            const res = await httpIdp.get(`/api/chats/${chatId}/media`, { params: { bucket, limit, offset } });
            return res.data?.items ?? [];
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async listPins(chatId) {
        try {
            const res = await httpIdp.get(`/api/chats/${chatId}/pins`);
            return res.data?.items ?? [];
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async pinMessage(chatId, messageId) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/pins`, { messageId });
            return res.data?.items ?? [];
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async unpinMessage(chatId, messageId) {
        try {
            const res = await httpIdp.delete(`/api/chats/${chatId}/pins/${messageId}`);
            return res.data?.items ?? [];
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async reactToMessage(chatId, messageId, reaction) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/messages/${messageId}/reactions`, { reaction });
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async deleteReaction(chatId, messageId) {
        try {
            const res = await httpIdp.delete(`/api/chats/${chatId}/messages/${messageId}/reactions`);
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async markMessageImportant(chatId, messageId) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/messages/${messageId}/important`);
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async unmarkMessageImportant(chatId, messageId) {
        try {
            const res = await httpIdp.delete(`/api/chats/${chatId}/messages/${messageId}/important`);
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async forwardMessage(targetChatId, sourceMessageId) {
        try {
            const res = await httpIdp.post(`/api/chats/${targetChatId}/messages/forward`, { sourceMessageId });
            return res.data.message;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async markRead(chatId, messageId) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/read`, { messageId });
            return res.data.readState;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async setTyping(chatId, isTyping) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/typing`, { isTyping });
            return res.data.typingState;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async startCall(chatId, kind = 'audio', options = {}) {
        try {
            const res = await httpIdp.post(`/api/chats/${chatId}/calls`, {
                kind,
                recipientUserId: options.recipientUserId ?? undefined,
                protocol: options.protocol || 'livekit-v1',
            });
            return res.data?.call;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async getCall(callId) {
        try {
            const res = await httpIdp.get(`/api/chats/calls/${callId}`);
            return res.data?.call;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async joinCall(callId) {
        try {
            const res = await httpIdp.post(`/api/chats/calls/${callId}/join`);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async declineCall(callId) {
        try {
            const res = await httpIdp.post(`/api/chats/calls/${callId}/decline`);
            return res.data?.call;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async leaveCall(callId) {
        try {
            const res = await httpIdp.post(`/api/chats/calls/${callId}/leave`);
            return res.data?.call;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async endCall(callId) {
        try {
            const res = await httpIdp.post(`/api/chats/calls/${callId}/end`);
            return res.data?.call;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    async recordCallQualityEvent(callId, payload) {
        try {
            const res = await httpIdp.post(`/api/chats/calls/${callId}/quality-events`, payload);
            return res.data;
        } catch (e) {
            normalizeAxiosError(e);
        }
    },

    buildStreamUrl(chatId, lastMessageId) {
        const normalized = IDP_ORIGIN.replace(/\/$/, '');
        const params = new URLSearchParams({ lastMessageId: String(Math.max(0, lastMessageId)) });
        return `${normalized}/api/chats/${chatId}/stream?${params.toString()}`;
    },

    resolveAssetUrl,
};
